import React, { createContext, useContext, useState } from "react";
import { cn } from "@hipotecar/ui/lib/utils";

type TabsContextType = {
	activeTab: string;
	setActiveTab: (value: string) => void;
};

const TabsContext = createContext<TabsContextType | undefined>(undefined);

interface TabsProps {
	defaultValue?: string;
	value?: string;
	children: React.ReactNode;
	className?: string;
	onValueChange?: (value: string) => void;
}

export function Tabs({
	defaultValue,
	value,
	children,
	className,
	onValueChange,
}: TabsProps) {
	const [activeInternal, setActiveInternal] = useState(
		defaultValue || value || "",
	);

	const isControlled = value !== undefined;
	const activeTab = isControlled ? value : activeInternal;

	const setActiveTab = (newValue: string) => {
		if (!isControlled) {
			setActiveInternal(newValue);
		}
		onValueChange?.(newValue);
	};

	return (
		<TabsContext.Provider value={{ activeTab, setActiveTab }}>
			<div className={cn("w-full", className)}>{children}</div>
		</TabsContext.Provider>
	);
}

interface TabsListProps {
	children: React.ReactNode;
	className?: string;
}

export function TabsList({ children, className }: TabsListProps) {
	return (
		<div
			className={cn(
				"no-scrollbar flex w-full space-x-1 overflow-x-auto rounded-xl bg-gray-200 p-1 sm:w-fit dark:bg-gray-950",
				className,
			)}
		>
			{children}
		</div>
	);
}

interface TabsTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	value: string;
	children: React.ReactNode;
}

export function TabsTrigger({ value, children, className, onClick, ...props }: TabsTriggerProps) {
	const context = useContext(TabsContext);
	if (!context) throw new Error("TabsTrigger must be used within Tabs");

	const isActive = context.activeTab === value;

	return (
		<button
			type="button"
			onClick={(e) => {
				context.setActiveTab(value);
				onClick?.(e);
			}}
			className={cn(
				"rounded-lg px-4 py-2 text-sm font-medium whitespace-nowrap transition-all",
				isActive
					? "dark:bg-card bg-white text-gray-900 shadow-sm ring-1 ring-gray-200 dark:text-white dark:ring-gray-700"
					: "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200",
				className,
			)}
			{...props}
		>
			{children}
		</button>
	);
}

interface TabsContentProps {
	value: string;
	children: React.ReactNode;
	className?: string;
}

export function TabsContent({ value, children, className }: TabsContentProps) {
	const context = useContext(TabsContext);
	if (!context) throw new Error("TabsContent must be used within Tabs");

	if (context.activeTab !== value) return null;

	return (
		<div className={cn("mt-2 duration-300 animate-in fade-in", className)}>
			{children}
		</div>
	);
}
